/**
 * Realtime STOMP qua WebSocket (TDD §9.2 — thông báo, cập nhật trạng thái đơn, Unified Inbox) — W11.
 * Một kết nối cho cả shell + remote (singleton). Máy chủ: notify-svc `/api/v1/notifications/ws` (qua gateway), CONNECT mang Bearer JWT,
 * SUBSCRIBE chỉ được nhận đích của tenant/người dùng mình: `/user/queue/inbox` (hộp thư của tôi) và `/topic/tenant.<tenantId>.<eventType>`
 * (sự kiện nghiệp vụ đã lọc capability ở máy chủ). Dữ liệu nhận về chỉ dùng để làm mới TanStack Query / hiện toast — KHÔNG sao chép vào store.
 */
import { Client, type IMessage, type StompSubscription } from '@stomp/stompjs';
import { useEffect } from 'react';
import { create } from 'zustand';
import { getAccessToken } from './auth';

export type RealtimeStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting';
interface RealtimeState {
  status: RealtimeStatus;
  lastError: string | null;
}
export const useRealtimeStatus = create<RealtimeState>(() => ({ status: 'idle', lastError: null }));

export interface RealtimeMessage<T = unknown> {
  destination: string;
  body: T;
  headers: Record<string, string>;
}
type Handler = (m: RealtimeMessage) => void;

function brokerUrl(): string {
  const base =
    import.meta.env.VITE_WS_BASE ??
    (import.meta.env.VITE_API_BASE && import.meta.env.VITE_API_BASE !== '/'
      ? import.meta.env.VITE_API_BASE
      : window.location.origin);
  return `${base.replace(/^http/, 'ws')}/api/v1/notifications/ws`;
}

const subs = new Map<string, { handlers: Set<Handler>; stomp?: StompSubscription | undefined }>();
let client: Client | null = null;
let wanted = false;

function ensureClient(): Client {
  if (client) return client;
  client = new Client({
    brokerURL: brokerUrl(),
    reconnectDelay: 5000,
    heartbeatIncoming: 20000,
    heartbeatOutgoing: 20000,
    beforeConnect: async () => {
      const token = await getAccessToken();
      if (client) client.connectHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      useRealtimeStatus.setState({
        status: useRealtimeStatus.getState().status === 'connected' ? 'reconnecting' : 'connecting',
      });
    },
    onConnect: () => {
      useRealtimeStatus.setState({ status: 'connected', lastError: null });
      for (const [dest, s] of subs) if (!s.stomp && client) s.stomp = client.subscribe(dest, (m) => dispatch(dest, m));
    },
    onWebSocketClose: () => {
      for (const s of subs.values()) s.stomp = undefined;
      if (wanted) useRealtimeStatus.setState({ status: 'reconnecting' });
    },
    onStompError: (f) => useRealtimeStatus.setState({ lastError: f.headers.message ?? 'STOMP error' }),
  });
  return client;
}

function dispatch(dest: string, m: IMessage) {
  const s = subs.get(dest);
  if (!s) return;
  let body: unknown = m.body;
  try {
    body = JSON.parse(m.body);
  } catch {
    /* văn bản thuần */
  }
  const msg: RealtimeMessage = { destination: dest, body, headers: m.headers as Record<string, string> };
  for (const h of s.handlers) h(msg);
}

/** Đăng ký nhận một đích; trả về hàm hủy. Kết nối mở lười khi có đăng ký đầu tiên, đóng khi hết đăng ký. */
export function subscribeRealtime(destination: string, handler: Handler): () => void {
  let s = subs.get(destination);
  if (!s) {
    s = { handlers: new Set() };
    subs.set(destination, s);
  }
  s.handlers.add(handler);
  const c = ensureClient();
  wanted = true;
  if (!c.active) c.activate();
  else if (c.connected && !s.stomp) s.stomp = c.subscribe(destination, (m) => dispatch(destination, m));
  return () => {
    const cur = subs.get(destination);
    if (!cur) return;
    cur.handlers.delete(handler);
    if (cur.handlers.size === 0) {
      cur.stomp?.unsubscribe();
      subs.delete(destination);
    }
    if (subs.size === 0 && client) {
      wanted = false;
      void client.deactivate();
      client = null;
      useRealtimeStatus.setState({ status: 'idle' });
    }
  };
}

/** Hook: gọi handler mỗi khi có tin ở đích (thường là `queryClient.invalidateQueries`). `destination` null → không đăng ký. */
export function useRealtime(destination: string | null, handler: Handler): void {
  useEffect(() => {
    if (!destination) return;
    return subscribeRealtime(destination, handler);
    // handler thay đổi mỗi render — người gọi bọc useCallback khi cần
  }, [destination, handler]);
}

/** Đích chuẩn. */
export const realtimeDestinations = {
  inbox: () => '/user/queue/inbox',
  tenantEvent: (tenantId: string, eventType: string) => `/topic/tenant.${tenantId}.${eventType}`,
};
